import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import ProgressRing from "@/components/ProgressRing";
import { Skeleton } from "@/components/ui/skeleton";
import { BookOpen, Flame, Award, PlayCircle, FileText, Calendar } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";

function CourseCard({ c }) {
    const pct = c.progress?.percent || 0;
    return (
        <Link to={`/student/courses/${c.id}`} className="mx-card overflow-hidden block group" data-testid={`course-card-${c.id}`}>
            <div className="aspect-video relative overflow-hidden bg-muted">
                {c.thumbnail_url && <img src={c.thumbnail_url} alt={c.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" />}
                <div className="absolute top-3 left-3"><Badge className="bg-white/90 text-foreground border-0 backdrop-blur">{c.level}</Badge></div>
            </div>
            <div className="p-5 flex items-center gap-4">
                <div className="flex-1 min-w-0">
                    <div className="text-[10px] uppercase tracking-widest font-bold text-muted-foreground">{c.category}</div>
                    <div className="font-bold text-base mt-1 line-clamp-2">{c.title}</div>
                    <div className="text-xs text-muted-foreground mt-1.5">{c.progress?.completed_lessons || 0} of {c.progress?.total_lessons || 0} lessons</div>
                </div>
                <ProgressRing percent={pct} size={56} stroke={6} color={pct >= 100 ? "#16A34A" : "#726AF0"} trackColor="hsl(var(--border))" />
            </div>
        </Link>
    );
}

export default function StudentDashboard() {
    const [data, setData] = useState(null);
    useEffect(() => { api.get("/dashboard/student").then((r) => setData(r.data)).catch(() => {}); }, []);
    if (!data) return <Skeleton className="h-96 rounded-2xl" />;

    const s = data.stats;
    const cont = data.continue_lesson;

    return (
        <div className="space-y-8" data-testid="student-dashboard">
            <div>
                <div className="text-xs uppercase tracking-widest font-bold text-muted-foreground">Your learning hub</div>
                <h1 className="font-display text-3xl lg:text-4xl mt-1">Pick up where you left off</h1>
            </div>

            {cont && (
                <div className="mx-card p-6 lg:p-8 bg-brand-gradient text-white relative overflow-hidden" data-testid="continue-lesson-card">
                    <div className="absolute inset-0 opacity-10" style={{ backgroundImage: "radial-gradient(circle at 80% 20%, white 1px, transparent 1px)", backgroundSize: "30px 30px" }} />
                    <div className="relative">
                        <div className="text-xs uppercase tracking-widest font-bold text-white/80">Continue learning</div>
                        <h2 className="font-display text-2xl lg:text-3xl mt-2">{cont.title}</h2>
                        {cont.module && <div className="text-white/80 mt-1.5">{cont.module.title}</div>}
                        <Link to={`/student/courses`}>
                            <Button className="mt-6 bg-white text-[#726AF0] hover:bg-white/95 rounded-xl font-semibold" data-testid="resume-lesson-btn">
                                <PlayCircle className="h-4 w-4 mr-2" /> Resume lesson
                            </Button>
                        </Link>
                    </div>
                </div>
            )}

            <div className="grid gap-4 grid-cols-2 lg:grid-cols-4">
                <div className="mx-card p-5" data-testid="stat-enrolled">
                    <div className="h-10 w-10 rounded-xl bg-[#F0EFFF] dark:bg-[#1C1A2E] flex items-center justify-center mb-3"><BookOpen className="h-5 w-5 text-[#726AF0]" /></div>
                    <div className="text-xs uppercase tracking-widest font-bold text-muted-foreground">Enrolled</div>
                    <div className="font-display text-3xl mt-1">{s.enrolled_courses}</div>
                </div>
                <div className="mx-card p-5" data-testid="stat-progress">
                    <div className="h-10 w-10 rounded-xl bg-[#F0EFFF] dark:bg-[#1C1A2E] flex items-center justify-center mb-3"><PlayCircle className="h-5 w-5 text-[#E64AA1]" /></div>
                    <div className="text-xs uppercase tracking-widest font-bold text-muted-foreground">Overall progress</div>
                    <div className="font-display text-3xl mt-1">{s.overall_progress}%</div>
                </div>
                <div className="mx-card p-5" data-testid="stat-streak">
                    <div className="h-10 w-10 rounded-xl amber-gradient bg-amber-gradient flex items-center justify-center mb-3"><Flame className="h-5 w-5 text-white" /></div>
                    <div className="text-xs uppercase tracking-widest font-bold text-muted-foreground">Streak</div>
                    <div className="font-display text-3xl mt-1">{s.streak.current} <span className="text-base text-muted-foreground">days</span></div>
                </div>
                <div className="mx-card p-5" data-testid="stat-certs">
                    <div className="h-10 w-10 rounded-xl bg-amber-gradient flex items-center justify-center mb-3"><Award className="h-5 w-5 text-white" /></div>
                    <div className="text-xs uppercase tracking-widest font-bold text-muted-foreground">Certificates</div>
                    <div className="font-display text-3xl mt-1">{s.certificates}</div>
                </div>
            </div>

            <div>
                <div className="flex items-center justify-between mb-4">
                    <h2 className="font-display text-2xl">My courses</h2>
                    <Link to="/student/courses" className="text-sm font-semibold text-[#726AF0] hover:underline">See all</Link>
                </div>
                {data.courses.length === 0 ? (
                    <div className="mx-card p-12 text-center">
                        <BookOpen className="h-10 w-10 mx-auto text-muted-foreground" />
                        <div className="mt-3 font-semibold">No courses yet</div>
                        <div className="text-sm text-muted-foreground">Your admin will assign you to a batch shortly.</div>
                    </div>
                ) : (
                    <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
                        {data.courses.map((c) => <CourseCard key={c.id} c={c} />)}
                    </div>
                )}
            </div>

            <div className="grid gap-6 lg:grid-cols-2">
                <Card className="mx-card">
                    <CardContent className="p-6">
                        <div className="flex items-center gap-2 mb-4"><Calendar className="h-4 w-4 text-[#E64AA1]" /><h3 className="font-bold">Upcoming assignments</h3></div>
                        {data.upcoming_assignments.length === 0 ? <div className="text-sm text-muted-foreground py-4">Nothing due. Nice.</div> :
                            <ul className="divide-y divide-border">
                                {data.upcoming_assignments.map((a) => (
                                    <li key={a.id} className="py-3 flex items-start gap-3" data-testid={`upcoming-assignment-${a.id}`}>
                                        <div className="h-9 w-9 rounded-lg bg-[#F0EFFF] dark:bg-[#1C1A2E] flex items-center justify-center"><FileText className="h-4 w-4 text-[#726AF0]" /></div>
                                        <div className="flex-1 min-w-0">
                                            <div className="font-semibold text-sm truncate">{a.title}</div>
                                            <div className="text-xs text-muted-foreground">Due {a.due_date ? new Date(a.due_date).toLocaleDateString() : "—"}</div>
                                        </div>
                                        <Badge variant={a.my_submission ? "secondary" : "outline"} className="text-[10px]">{a.my_submission ? a.my_submission.status : "Not submitted"}</Badge>
                                    </li>
                                ))}
                            </ul>
                        }
                    </CardContent>
                </Card>
                <Card className="mx-card">
                    <CardContent className="p-6">
                        <div className="flex items-center gap-2 mb-4"><Award className="h-4 w-4 text-[#FFA81B]" /><h3 className="font-bold">Recent quiz scores</h3></div>
                        {data.recent_attempts.length === 0 ? <div className="text-sm text-muted-foreground py-4">No attempts yet</div> :
                            <ul className="divide-y divide-border">
                                {data.recent_attempts.map((r) => (
                                    <li key={r.id} className="py-3 flex justify-between items-center">
                                        <div>
                                            <div className="font-semibold text-sm">{r.quiz_title}</div>
                                            <div className="text-xs text-muted-foreground">{new Date(r.submitted_at).toLocaleString()}</div>
                                        </div>
                                        <div className={`font-display text-xl ${r.passed ? "text-[#16A34A]" : "text-[#E11D48]"}`}>{r.percentage}%</div>
                                    </li>
                                ))}
                            </ul>
                        }
                    </CardContent>
                </Card>
            </div>
        </div>
    );
}
